import { ChangeDetectorRef, Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Table } from 'primeng/table';
import { MessageService } from 'primeng/api';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
interface DueQuotation {
  quotationNo: string;
  quotationDate: Date;
  invoiceNo: string;
  lastInvoiceDate: Date;
  customerName: string;
  customerEmail: string;
  price: number;
  totalPaid: number;
  email:string;
  outstandingAmount: number;
  dueDate: Date;
  country:string;
  customerAddress:string;
  project:string;
  delayedDays: number;
  status: 'Pending' | 'Overdue';
  customerType?: string;
  jobNumber?: string;    // Add this
  poNumber?: string;     // Add this
  invoices?: any[];      // Add this
  payments?: any[];      // Add this
  
}

@Component({
  selector: 'app-due-quotation-dialog',
  templateUrl: './due-quotation-dialog.component.html',
  styleUrls: ['./due-quotation-dialog.component.scss'],
  providers: [MessageService]
})
export class DueQuotationDialogComponent implements OnInit {
  @ViewChild('dt') table!: Table;

  dueQuotations: DueQuotation[] = [];
  filteredDueQuotations: DueQuotation[] = [];
  cols: any[] = [];

  // Filter properties
  globalFilter: string = '';
  selectedStatus: any = null;
  dateRange: Date[] = [];
  delayedDaysRange: number[] = [0, 30];
  minPrice: number | null = null;
  maxPrice: number | null = null;
  selectedCustomerTypes: string[] = [];
  showAdvancedFilters: boolean = false;
  customerNameOptions: { label: string, value: string }[] = [];
  selectedCustomerName: string = '';
  lastInvoiceDateRange: Date[] = [];

  // Status options
  statusOptions: any[] = [
    { label: 'All Statuses', value: null },
    { label: 'Pending', value: 'Pending' },
    { label: 'Overdue', value: 'Overdue' }
  ];

  // Customer type options
  customerTypes: any[] = [
    { label: 'Regular', value: 'Regular' },
    { label: 'VIP', value: 'VIP' },
    { label: 'Wholesale', value: 'Wholesale' }
  ];

  // Summary statistics
  totalDue: number = 0;
  overdueTotal: number = 0;
  pendingTotal: number = 0;
  selectedQuotation: DueQuotation | null = null;
  showDetailPanel: boolean = false;
  expandedQuotations: Set<string> = new Set()
  showEmailDialog: boolean = false;
  emailContent: any = {};
  get mailtoUrl(): string {
    return this.createMailtoUrl(
      this.emailContent.to,
      this.emailContent.subject,
      this.emailContent.body
    );
  }
  isMaximized: boolean = false;
  fallbackTimer: any;
  constructor(
    public ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.initializeColumns();
    this.loadDueQuotations();
    this.calculateSummaryStats();
  }

  initializeColumns(): void {
    this.cols = [
      { field: 'quotationNo', header: 'Quotation', width: '180px', sortable: true },
      { field: 'customerName', header: 'Customer', width: '220px', sortable: true },
      { field: 'invoiceNo', header: 'Last Invoice No', width: '150px', sortable: true },
      { field: 'lastInvoiceDate', header: 'Last Invoice', width: '140px', sortable: true },
      { field: 'dueDate', header: 'Due Date', width: '140px', sortable: true },
      { field: 'price', header: 'Amount', width: '150px', sortable: true },
      { field: 'status', header: 'Status', width: '120px', sortable: true }
    ];
  }

  loadDueQuotations(): void {
    const itemsWithDueDate = this.config.data.workflowItems?.filter((item: any) => {
      const hasInvoicesWithDueDate = item.invoices &&
        item.invoices.length > 0 &&
        item.invoices.some((invoice: any) => invoice.paymentDueDate);
      return hasInvoicesWithDueDate || item.blDate;
    }) || [];

    this.dueQuotations = itemsWithDueDate
      .map((item: any) => {
        const lastInvoice = this.getLastInvoice(item.invoices || []);
        const totalPaymentPrice = this.calculateTotalPayments(item.payments || []);
        const totalInvoicePrice = this.calculateTotalInvoices(item.invoices || []);
        const dueDateStr = lastInvoice?.paymentDueDate || item.paymentDueDate;
        const delayedDays = this.calculateDelayedDays(dueDateStr);
        const rawOutstandingAmount = totalInvoicePrice - totalPaymentPrice;
        const outstandingAmount = Math.round(rawOutstandingAmount * 100) / 100;
        console.log(`Quotation ${item.quotationNo}: Invoice Total = ${totalInvoicePrice}, Payment Total = ${totalPaymentPrice}, Outstanding = ${outstandingAmount}`);

        return {
          quotationNo: item.quotationNo || '',
          quotationDate: this.parseDate(item.quotationDate),
          invoiceNo: lastInvoice?.invoiceNo || '',
          lastInvoiceDate: this.parseDate(lastInvoice?.date),
          customerName: item.customerName || '',
          customerEmail: item.email || '',
          price: totalInvoicePrice,
          totalPaid: totalPaymentPrice,
          outstandingAmount: outstandingAmount,
          dueDate: this.parseDate(dueDateStr),
          delayedDays: delayedDays,
          status: delayedDays > 0 ? 'Overdue' : 'Pending',
          customerType: item.customerType || 'Regular',
          jobNumber: item.jobNumber || '',
          poNumber: item.poNumber || '',
          invoices: item.invoices || [],
          payments: item.payments || [],
          project: item.project || '',
          country: item.country || '',
          customerAddress: item.customerAddress || '',
          
        } as DueQuotation & { outstandingAmount: number };
      }).filter((item: DueQuotation & { outstandingAmount: number }) => item.outstandingAmount > 0.01);

    console.log('Final Due Quotations:', this.dueQuotations);

    const uniqueCustomerNames = [...new Set(this.dueQuotations.map(item => item.customerName).filter(name => name))];
    this.customerNameOptions = uniqueCustomerNames.map(name => ({
      label: name,
      value: name
    }));

    this.filteredDueQuotations = [...this.dueQuotations];
    console.log('Filtered Due Quotations:', this.filteredDueQuotations);
  }

  parseDate(dateStr: string): Date | null {
    if (!dateStr) return null;
    try {
      // Handle various date formats
      return new Date(dateStr.includes('/') ? dateStr.replace(/\//g, '-') : dateStr);
    } catch (error) {
      console.error('Error parsing date:', error);
      return null;
    }
  }

  getLastInvoice(invoices: any[]): any {
    if (!invoices || invoices.length === 0) return null;

    return invoices.sort((a, b) => {
      const dateA = this.parseDate(a.date) || new Date(0);
      const dateB = this.parseDate(b.date) || new Date(0);
      return dateB.getTime() - dateA.getTime();
    })[0];
  }
  isQuotationExpanded(quotationNo: string): boolean {
    return this.expandedQuotations.has(quotationNo);
  }


  calculateTotalPayments(payments: any[]): number {
    if (!payments || payments.length === 0) return 0;
    return payments.reduce((total, payment) => total + (payment.paymentPrice || 0), 0);
  }

  calculateTotalInvoices(invoices: any[]): number {
    if (!invoices || invoices.length === 0) return 0;
    return invoices.reduce((total, invoice) => total + (invoice.invoicePrice || 0), 0);
  }

  calculateDelayedDays(paymentDueDateStr: string): number {
    if (!paymentDueDateStr) return 0;

    try {
      const paymentDueDate = this.parseDate(paymentDueDateStr);
      if (!paymentDueDate) return 0;

      const today = new Date();

      // Normalize both dates to midnight for accurate day comparison
      paymentDueDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      const diffTime = today.getTime() - paymentDueDate.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      // Return both positive (overdue) and negative (pending) values
      return diffDays;
    } catch (error) {
      console.error('Error calculating delayed days:', error);
      return 0;
    }
  }
  getDelayedDaysLabel(delayedDays: number): string {
    if (delayedDays === 0) {
      return 'Due today';
    } else if (delayedDays > 0) {
      // Overdue payments
      return delayedDays === 1 ? '1 day overdue' : `${delayedDays} days overdue`;
    } else {
      // Pending payments (negative values)
      const pendingDays = Math.abs(delayedDays);
      return pendingDays === 1 ? '1 day pending' : `${pendingDays} days pending`;
    }
  }

  getDelayedDaysSeverity(delayedDays: number): string {
    if (delayedDays > 0) {
      return 'danger'; // Overdue - red
    } else if (delayedDays === 0) {
      return 'warning'; // Due today - orange/yellow
    } else {
      return 'success'; // Pending - green
    }
  }

  getDelayedDaysIcon(delayedDays: number): string {
    if (delayedDays > 0) {
      return 'pi pi-exclamation-triangle'; // Overdue
    } else if (delayedDays === 0) {
      return 'pi pi-clock'; // Due today
    } else {
      return 'pi pi-check-circle'; // Pending
    }
  }

  calculateSummaryStats(): void {
    // Calculate based on outstanding amounts instead of total invoice amounts
    this.totalDue = this.filteredDueQuotations.reduce((sum, item) => sum + item.outstandingAmount, 0);
    this.overdueTotal = this.filteredDueQuotations
      .filter(item => item.status === 'Overdue')
      .reduce((sum, item) => sum + item.outstandingAmount, 0);
    this.pendingTotal = this.filteredDueQuotations
      .filter(item => item.status === 'Pending')
      .reduce((sum, item) => sum + item.outstandingAmount, 0);
  }


  // Filter methods
  filterByStatus(): void {
    if (!this.selectedStatus || this.selectedStatus.value === null) {
      this.filteredDueQuotations = [...this.dueQuotations];
    } else {
      this.filteredDueQuotations = this.dueQuotations.filter(
        item => item.status === this.selectedStatus.value
      );
    }
    this.calculateSummaryStats();
  }

  toggleAdvancedFilters(): void {
    this.showAdvancedFilters = !this.showAdvancedFilters;
  }

  applyAdvancedFilters(): void {
    this.filteredDueQuotations = this.dueQuotations.filter(item => {
      // Customer name filter
      if (this.selectedCustomerName &&
        item.customerName !== this.selectedCustomerName) {
        return false;
      }

      // Last invoice date range filter
      if (this.lastInvoiceDateRange && this.lastInvoiceDateRange.length === 2) {
        if (!item.lastInvoiceDate) return false;

        const lastInvoiceDate = item.lastInvoiceDate.getTime();
        const start = this.lastInvoiceDateRange[0].getTime();
        const end = this.lastInvoiceDateRange[1].getTime();
        if (lastInvoiceDate < start || lastInvoiceDate > end) return false;
      }

      return true;
    });

    this.calculateSummaryStats();
    this.messageService.add({
      severity: 'success',
      summary: 'Filters Applied',
      detail: `${this.filteredDueQuotations.length} quotations match your criteria`
    });
  }

  // resetAdvancedFilters method remains the same
  resetAdvancedFilters(): void {
    this.selectedCustomerName = '';
    this.lastInvoiceDateRange = [];
    this.loadDueQuotations();
  }

  // clearFilters method remains the same
  clearFilters(): void {
    this.globalFilter = '';
    this.selectedStatus = null;
    this.resetAdvancedFilters();
    this.filteredDueQuotations = [...this.dueQuotations];
    this.calculateSummaryStats();

    if (this.table) {
      this.table.clear();
    }
  }
  // Action methods

  viewQuotation(quotation: DueQuotation): void {
    const quotationNo = quotation.quotationNo;

    if (this.expandedQuotations.has(quotationNo)) {
      // If already expanded, collapse it
      this.expandedQuotations.delete(quotationNo);
    } else {
      // If collapsed, expand it
      this.expandedQuotations.add(quotationNo);
    }

    this.messageService.add({
      severity: 'info',
      summary: 'Details Toggled',
      detail: `${this.expandedQuotations.has(quotationNo) ? 'Showing' : 'Hiding'} details for ${quotationNo}`
    });
  }

  closeDetailPanel(): void {
    this.showDetailPanel = false;
    this.selectedQuotation = null;
  }

  generatePDFReport(quotation: DueQuotation): void {
    try {
      this.createProfessionalPDF(quotation);

      this.messageService.add({
        severity: 'success',
        summary: 'PDF Generated',
        detail: `Professional report generated for ${quotation.quotationNo}`
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to generate PDF report'
      });
    }
  }
  private loadImage(src: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL());
      };
      img.onerror = (error) => {
        console.error('Error loading image:', error);
        reject(error);
      };
      img.src = src;
    });
  }


private async createProfessionalPDF(quotation: DueQuotation): Promise<void> {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  let yPosition = 15;
  let currentPage = 1;

  // Define consistent spacing between sections
  const verticalSpacingBetweenSections = 8;

  // Set document metadata
  pdf.setProperties({
    title: `Payment Report - ${quotation.quotationNo}`,
    subject: 'Professional Payment Report',
    author: 'Kikblox Finance Department',
    keywords: 'payment, report, invoice, financial',
    creator: 'Kikblox Reporting System'
  });

  // Add watermark/background
  this.addProfessionalWatermark(pdf, pageWidth, pageHeight);

  // Header Section
  yPosition = await this.addCorporateHeader(pdf, margin, yPosition, pageWidth, quotation);

  // Content sections with proper page break handling
  const sections = [
    {
      method: (pdf: jsPDF, x: number, y: number, width: number) =>
        this.addClientInformationSection(pdf, x, y, width, quotation),
      height: 45
    },
    {
      method: (pdf: jsPDF, x: number, y: number, width: number) =>
        this.addFinancialOverviewSection(pdf, x, y, width, quotation),
      height: 55
    },
    {
      method: (pdf: jsPDF, x: number, y: number, width: number) =>
        this.addPaymentStatusSection(pdf, x, y, width, quotation),
      height: 30
    }
  ];

  for (const section of sections) {
    const pageCheck = this.checkPageBreak(pdf, yPosition, section.height, pageHeight, margin, currentPage);
    yPosition = pageCheck.yPos;
    currentPage = pageCheck.pageNum;

    yPosition = section.method(pdf, margin + 5, yPosition, pageWidth - 2 * margin - 10);
    yPosition += verticalSpacingBetweenSections; // Consistent spacing after each section
  }

  // Invoice Details Table with proper spacing
  if (quotation.invoices && quotation.invoices.length > 0) {
    const tableHeight = this.estimateTableHeight(quotation.invoices.length);
    const pageCheck = this.checkPageBreak(pdf, yPosition, tableHeight, pageHeight, margin, currentPage);
    yPosition = pageCheck.yPos;
    currentPage = pageCheck.pageNum;

    yPosition = this.addInvoiceDetailsTable(pdf, yPosition, quotation.invoices, quotation.payments || []);
    yPosition += verticalSpacingBetweenSections; // Add consistent gap after invoice table
  }

  // Payment History Table with proper spacing
  if (quotation.payments && quotation.payments.length > 0) {
    const tableHeight = this.estimateTableHeight(quotation.payments.length);
    const pageCheck = this.checkPageBreak(pdf, yPosition, tableHeight, pageHeight, margin, currentPage);
    yPosition = pageCheck.yPos;
    currentPage = pageCheck.pageNum;

    yPosition = this.addPaymentHistoryTable(pdf, yPosition, quotation.payments);
    yPosition += verticalSpacingBetweenSections; // Add consistent gap after payment history table
  }

  // Terms and Notes section with proper spacing
  const pageCheckFinal = this.checkPageBreak(pdf, yPosition, 45, pageHeight, margin, currentPage);
  yPosition = pageCheckFinal.yPos;
  currentPage = pageCheckFinal.pageNum;
  yPosition = this.addTermsAndNotes(pdf, yPosition, pageWidth - 2 * margin);

  // Footer on all pages
  this.addFooterToAllPages(pdf, quotation);

  // Save PDF
  const fileName = `Kikblox_Payment_Report_${quotation.quotationNo}_${new Date().toISOString().slice(0, 10)}.pdf`;
  pdf.save(fileName);
}


  private addFooterToAllPages(pdf: jsPDF, quotation: DueQuotation): void {
    const pageCount = pdf.getNumberOfPages();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      this.addCorporateFooter(pdf, quotation, pageWidth, pageHeight, i);
    }
  }

  // Helper method to check for page breaks
  private checkPageBreak(pdf: jsPDF, currentY: number, neededHeight: number, pageHeight: number, margin: number, currentPage: number): { yPos: number, pageNum: number } {
    if (currentY + neededHeight > pageHeight - margin - 20) { // Extra margin for footer
      pdf.addPage();
      currentPage++;
      this.addPageHeader(pdf, margin, currentPage);
      return { yPos: 25, pageNum: currentPage }; // Start content lower on new pages
    }
    return { yPos: currentY, pageNum: currentPage };
  }

  // Add page header for subsequent pages
  private addPageHeader(pdf: jsPDF, margin: number, pageNumber: number): void {
    const pageWidth = pdf.internal.pageSize.getWidth();

    // Page header background
    pdf.setFillColor(13, 36, 56);
    pdf.rect(0, 0, pageWidth, 15, 'F');

    // Page number
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(255, 255, 255);
    pdf.text(`Page ${pageNumber}`, pageWidth - margin, 10, { align: 'right' });

    // Document title continuation
    pdf.text('Payment Report (Continued)', margin, 10);
  }
  // Estimate table height based on row count
  private estimateTableHeight(rowCount: number): number {
    const headerHeight = 10;
    const rowHeight = 8;
    const titleSpace = 15;
    const padding = 10;
    return headerHeight + (rowCount * rowHeight) + titleSpace + padding;
  }
private async addCorporateHeader(pdf: jsPDF, margin: number, yPosition: number, pageWidth: number, quotation: DueQuotation): Promise<number> {
  // Header background
  pdf.setFillColor(13, 36, 56);
  pdf.rect(0, 0, pageWidth, 55, 'F'); // Increased height for additional info

  // Add logo
  try {
    const logoImg = await this.loadImage('assets/img/kikblox_logo.webp');
    pdf.addImage(logoImg, 'WEBP', margin, yPosition - 5, 40, 20);
  } catch (error) {
    pdf.setFillColor(255, 255, 255);
    pdf.roundedRect(margin, yPosition - 5, 40, 20, 2, 2, 'FD');
    pdf.setFontSize(10);
    pdf.setTextColor(13, 36, 56);
    pdf.text('KIKBLOX', margin + 10, yPosition + 5);
  }

  // Report title
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(255, 255, 255);
  pdf.text('PAYMENT REPORT', pageWidth - margin, yPosition + 5, { align: 'right' });

  // Report subtitle
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(200, 210, 220);
  pdf.text('Financial Transaction Summary', pageWidth - margin, yPosition + 12, { align: 'right' });

  // Report details - Enhanced with Job Number and PO Number
  pdf.setFontSize(9);
  pdf.text(`Report #: ${quotation.quotationNo}`, pageWidth - margin, yPosition + 18, { align: 'right' });
  pdf.text(`Job Number: ${quotation.jobNumber || 'N/A'}`, pageWidth - margin, yPosition + 23, { align: 'right' });
  pdf.text(`PO Number: ${quotation.poNumber || 'N/A'}`, pageWidth - margin, yPosition + 28, { align: 'right' });
  pdf.text(`Generated: ${new Date().toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  })}`, pageWidth - margin, yPosition + 33, { align: 'right' });

  return 60; // Increased return value for additional space
}

  private addTermsAndNotes(pdf: jsPDF, y: number, width: number): number {
    const margin = 15;
    const sectionHeight = 40;

    // Section container
    pdf.setFillColor(245, 247, 250);
    pdf.setDrawColor(200, 205, 210);
    pdf.roundedRect(margin, y, width, sectionHeight, 3, 3, 'FD');

    // Section title
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(13, 36, 56);
    pdf.text('Notes & Terms', margin + 5, y + 8);

    // Divider
    pdf.setDrawColor(200, 205, 210);
    pdf.line(margin + 5, y + 10, margin + width - 5, y + 10);

    // Notes content
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(60, 65, 70);

    const notes = [
      '1. All payments should be made in USD unless otherwise specified.',
      '2. Please reference the invoice number when making payments.',
      '3. Contact accounts@kikblox.com for any payment discrepancies.'
    ];

    notes.forEach((note, index) => {
      pdf.text(note, margin + 5, y + 15 + (index * 5));
    });

    return y + sectionHeight;
  }

private addPaymentHistoryTable(pdf: jsPDF, startY: number, payments: any[]): number {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const margin = 15;
  const tableWidth = pageWidth - (2 * margin);
  let yPos = startY;
  
  // Increased table width - using 90% of page width instead of 80%
  const colWidths = [40, 35, 30, 35, 30]; // Base widths
  const totalColWidth = colWidths.reduce((a, b) => a + b, 0);
  const scaleFactor = (tableWidth * 0.9) / totalColWidth; // Increased from 0.8 to 0.9
  const scaledColWidths = colWidths.map(width => width * scaleFactor);
  const centeredTableWidth = scaledColWidths.reduce((a, b) => a + b, 0);
  const tableStartX = (pageWidth - centeredTableWidth) / 2;
  
  // Adjusted row heights and spacing
  const rowHeight = 10;
  const headerHeight = 12;
  
  // Table title - reduced font size and spacing
  pdf.setFontSize(12); // Reduced from 14
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(13, 36, 56);
  pdf.text(' Payment Transaction History', pageWidth / 2, yPos, { align: 'center' });
  yPos += 8; // Reduced from 10
  
  // Subtitle - reduced font size and spacing
  pdf.setFontSize(8); // Reduced from 9
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(100, 105, 110);
  pdf.text('Detailed record of all payment transactions', pageWidth / 2, yPos, { align: 'center' });
  yPos += 6; // Reduced from 8
  
  // Table header with gradient effect
  pdf.setFillColor(13, 36, 56);
  pdf.roundedRect(tableStartX, yPos, centeredTableWidth, headerHeight, 2, 2, 'F');
  
  const headers = ['Invoice Number', 'Payment Date', 'Amount (USD)', 'Transaction Status', 'Reference'];
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(8); // Reduced from 10
  pdf.setFont('helvetica', 'bold');
  
  let xPos = tableStartX;
  headers.forEach((header, index) => {
    const cellCenter = xPos + (scaledColWidths[index] / 2);
    pdf.text(header, cellCenter, yPos + 8, { align: 'center' });
    xPos += scaledColWidths[index];
  });
  
  yPos += headerHeight;
  
  // Table rows with professional styling
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7); // Reduced from 9
  
  payments.forEach((payment, index) => {
    xPos = tableStartX;
    
    // Alternating row colors with subtle difference
    if (index % 2 === 0) {
      pdf.setFillColor(248, 250, 252);
    } else {
      pdf.setFillColor(255, 255, 255);
    }
    pdf.rect(xPos, yPos, centeredTableWidth, rowHeight, 'F');
    
    // Row border
    pdf.setDrawColor(230, 235, 240);
    pdf.setLineWidth(0.3);
    pdf.rect(xPos, yPos, centeredTableWidth, rowHeight, 'D');
    
    // Determine payment status with enhanced logic
    const isCompleted = payment.paymentReceived === true || payment.paymentReceived === 'true';
    const statusText = isCompleted ? '✅ COMPLETED' : '⏳ PENDING';
    const statusColor: [number, number, number] = isCompleted ? [0, 150, 0] : [255, 140, 0];
    
    const rowData = [
      payment.invoiceNo || 'N/A',
      payment.paymentReceivedDate ? new Date(payment.paymentReceivedDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }) : 'Pending',
      this.formatCurrency(payment.paymentPrice || 0),
      statusText,
      payment.reference || `REF-${payment.invoiceNo?.slice(-4) || '0000'}`
    ];
    
    rowData.forEach((data, colIndex) => {
      const cellCenter = xPos + (scaledColWidths[colIndex] / 2);
      
      // Special formatting for different columns
      if (colIndex === 2) { // Amount column
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(13, 36, 56);
      } else if (colIndex === 3) { // Status column
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(...statusColor);
      } else {
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(60, 65, 70);
      }
      
      pdf.text(data.toString(), cellCenter, yPos + 7, { align: 'center' });
      xPos += scaledColWidths[colIndex];
    });
    
    yPos += rowHeight;
  });
  
  // Table summary footer - reduced font size
  pdf.setFillColor(240, 245, 248);
  pdf.rect(tableStartX, yPos, centeredTableWidth, 8, 'F');
  pdf.setDrawColor(13, 36, 56);
  pdf.setLineWidth(0.5);
  pdf.line(tableStartX, yPos, tableStartX + centeredTableWidth, yPos);
  
  pdf.setFontSize(6); // Reduced from 8
  pdf.setFont('helvetica', 'italic');
  pdf.setTextColor(100, 105, 110);
  pdf.text(`Total Transactions: ${payments.length}`, tableStartX + 5, yPos + 6);
  
  const completedCount = payments.filter(p => p.paymentReceived === true || p.paymentReceived === 'true').length;
  pdf.text(`Completed: ${completedCount} | Pending: ${payments.length - completedCount}`, 
    tableStartX + centeredTableWidth - 5, yPos + 6, { align: 'right' });
  
  return yPos + 15;
}


private addInvoiceDetailsTable(pdf: jsPDF, startY: number, invoices: any[], payments: any[]): number {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const margin = 15;
  let yPos = startY;
  
  // Increased table width to match payment table style (90% of page width)
  const tableWidth = pageWidth - (2 * margin);
  const colWidths = [30, 25, 30, 25, 30, 20];
  const totalColWidth = colWidths.reduce((a, b) => a + b, 0);
  const scaleFactor = (tableWidth * 0.9) / totalColWidth; // Use 90% width for consistency
  const scaledColWidths = colWidths.map(width => width * scaleFactor);
  const centeredTableWidth = scaledColWidths.reduce((a, b) => a + b, 0);
  const tableStartX = (pageWidth - centeredTableWidth) / 2;
  
  const minRowHeight = 10;
  const headerHeight = 12;
  
  // Table title - centered and professional (matching payment table style)
  pdf.setFontSize(12); // Reduced from 14 for consistency
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(13, 36, 56);
  pdf.text('Invoice Details', pageWidth / 2, yPos, { align: 'center' });
  yPos += 8; // Reduced spacing
  
  // Subtitle - matching payment table style
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(100, 105, 110);
  pdf.text('Comprehensive overview of all invoice transactions and status', pageWidth / 2, yPos, { align: 'center' });
  yPos += 6; // Reduced spacing
  
  // Table header with gradient effect (matching payment table)
  pdf.setFillColor(13, 36, 56);
  pdf.roundedRect(tableStartX, yPos, centeredTableWidth, headerHeight, 2, 2, 'F');
  
  const headers = ['Invoice #', 'Date', 'Amount', 'Due Date', 'Status', 'BL Date'];
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(8); // Reduced from 9
  pdf.setFont('helvetica', 'bold');
  
  let xPos = tableStartX;
  headers.forEach((header, index) => {
    const cellCenter = xPos + (scaledColWidths[index] / 2);
    pdf.text(header, cellCenter, yPos + 8, { align: 'center' });
    xPos += scaledColWidths[index];
  });
  
  yPos += headerHeight;
  
  // Table rows with dynamic height and professional styling
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7); // Reduced from 8
  
  invoices.forEach((invoice, index) => {
    xPos = tableStartX;
    
    // Calculate row height based on content
    let maxCellHeight = minRowHeight;
    const status = this.getInvoiceStatus(invoice, payments);
    const rowData = [
      invoice.invoiceNo || 'N/A',
      invoice.date ? new Date(invoice.date).toLocaleDateString() : 'N/A',
      this.formatCurrency(invoice.invoicePrice || 0),
      invoice.paymentDueDate ? new Date(invoice.paymentDueDate).toLocaleDateString() : 'N/A',
      status,
      invoice.blDate ? new Date(invoice.blDate).toLocaleDateString() : 'N/A'
    ];
    
    // Check if any cell needs more height
    rowData.forEach((data, colIndex) => {
      const cellWidth = scaledColWidths[colIndex] - 6;
      const lines = pdf.splitTextToSize(data.toString(), cellWidth);
      const cellHeight = Math.max(minRowHeight, lines.length * 4 + 4);
      maxCellHeight = Math.max(maxCellHeight, cellHeight);
    });
    
    // Alternating row colors with subtle difference (matching payment table)
    if (index % 2 === 0) {
      pdf.setFillColor(248, 250, 252);
    } else {
      pdf.setFillColor(255, 255, 255);
    }
    pdf.rect(tableStartX, yPos, centeredTableWidth, maxCellHeight, 'F');
    
    // Row border (matching payment table style)
    pdf.setDrawColor(230, 235, 240);
    pdf.setLineWidth(0.3);
    pdf.rect(tableStartX, yPos, centeredTableWidth, maxCellHeight, 'D');
    
    // Add cell content with centered alignment
    rowData.forEach((data, colIndex) => {
      const cellCenter = xPos + (scaledColWidths[colIndex] / 2);
      const cellWidth = scaledColWidths[colIndex] - 6;
      
      // Set text color and font based on column
      if (colIndex === 2) { // Amount column
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(13, 36, 56);
      } else if (colIndex === 4) { // Status column
        pdf.setFont('helvetica', 'bold');
        const statusColor = this.getStatusColor(invoice, payments);
        pdf.setTextColor(...statusColor);
      } else {
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(60, 65, 70);
      }
      
      // Add text with proper wrapping and center alignment
      const lines = pdf.splitTextToSize(data.toString(), cellWidth);
      lines.forEach((line: string, lineIndex: number) => {
        pdf.text(line, cellCenter, yPos + 7 + (lineIndex * 4), { align: 'center' });
      });
      
      xPos += scaledColWidths[colIndex];
    });
    
    yPos += maxCellHeight;
  });
  
  // Table summary footer (similar to payment table)
  pdf.setFillColor(240, 245, 248);
  pdf.rect(tableStartX, yPos, centeredTableWidth, 8, 'F');
  pdf.setDrawColor(13, 36, 56);
  pdf.setLineWidth(0.5);
  pdf.line(tableStartX, yPos, tableStartX + centeredTableWidth, yPos);
  
  pdf.setFontSize(6);
  pdf.setFont('helvetica', 'italic');
  pdf.setTextColor(100, 105, 110);
  pdf.text(`Total Invoices: ${invoices.length}`, tableStartX + 5, yPos + 6);
  
  const paidCount = invoices.filter(invoice => this.getInvoiceStatus(invoice, payments).includes('PAID')).length;
  pdf.text(`Paid: ${paidCount} | Pending: ${invoices.length - paidCount}`, 
    tableStartX + centeredTableWidth - 5, yPos + 6, { align: 'right' });
  
  return yPos + 15;
}


  private getInvoiceStatus(invoice: any, payments: any[]): string {
    // First check if there's a completed payment for this invoice
    if (this.isInvoiceCompleted(invoice.invoiceNo, payments)) {
      return 'Completed';
    }

    const today = new Date();
    const dueDate = invoice.paymentDueDate ? new Date(invoice.paymentDueDate) : null;

    if (invoice.paid) return 'Paid';
    if (dueDate && dueDate < today) return 'Overdue';
    return 'Pending';
  }

  private isInvoiceCompleted(invoiceNo: string, payments: any[]): boolean {
    return payments.some(payment =>
      payment.invoiceNo === invoiceNo && payment.paymentReceived
    );
  }

  private getStatusColor(invoice: any, payments: any[]): [number, number, number] {
    if (this.isInvoiceCompleted(invoice.invoiceNo, payments)) {
      return [0, 128, 0]; // Green for completed
    }

    if (invoice.paid) return [0, 128, 0]; // Green for paid

    const today = new Date();
    const dueDate = invoice.paymentDueDate ? new Date(invoice.paymentDueDate) : null;

    if (dueDate && dueDate < today) return [200, 0, 0]; // Red for overdue
    return [255, 165, 0]; // Orange for pending
  }


private addClientInformationSection(pdf: jsPDF, x: number, y: number, width: number, quotation: DueQuotation): number {
  const sectionHeight = 50; // Increased height

  // Section container with subtle shadow effect
  pdf.setFillColor(250, 252, 255);
  pdf.setDrawColor(180, 190, 200);
  pdf.setLineWidth(0.5);
  pdf.roundedRect(x, y, width, sectionHeight, 4, 4, 'FD');

  // Section title with icon effect
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(13, 36, 56);
  pdf.text('Client Information', x + 12, y + 10);

  // Professional divider
  pdf.setDrawColor(13, 36, 56);
  pdf.setLineWidth(1);
  pdf.line(x + 12, y + 12, x + width - 12, y + 12);

  // Client details in organized columns
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(60, 65, 70);

  const col1X = x + 12;
  const col2X = x + width / 2 + 5;

  // Column 1 - Labels in bold
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(13, 36, 56);
  pdf.text('Client Name:', col1X, y + 22);
  pdf.text('Job Number:', col1X, y + 28);
  pdf.text('PO Number:', col1X, y + 34);
  pdf.text('Project:', col1X, y + 40);

  // Column 1 - Values
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(60, 65, 70);
  pdf.text(quotation.customerName || 'N/A', col1X + 25, y + 22);
  pdf.text(quotation.jobNumber || 'N/A', col1X + 25, y + 28);
  pdf.text(quotation.poNumber || 'N/A', col1X + 25, y + 34);
  pdf.text(quotation.project || 'N/A', col1X + 25, y + 40);

  // Column 2 - Labels in bold
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(13, 36, 56);
  pdf.text('Email:', col2X, y + 22);
  pdf.text('Country:', col2X, y + 28);
  pdf.text('Address:', col2X, y + 34);

  // Column 2 - Values
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(60, 65, 70);
  pdf.text(quotation.customerEmail || 'N/A', col2X + 20, y + 22);
  pdf.text(quotation.country || 'N/A', col2X + 20, y + 28);
  
  // Handle long address with text wrapping
  const addressLines = pdf.splitTextToSize(quotation.customerAddress || 'N/A', width / 2 - 30);
  addressLines.forEach((line: string, index: number) => {
    pdf.text(line, col2X + 20, y + 34 + (index * 4));
  });

  return y + sectionHeight + 5;
}

  private addFinancialOverviewSection(pdf: jsPDF, x: number, y: number, width: number, quotation: DueQuotation): number {
    const sectionHeight = 50;

    // Section container
    pdf.setFillColor(255, 255, 255);
    pdf.setDrawColor(200, 205, 210);
    pdf.roundedRect(x, y, width, sectionHeight, 3, 3, 'FD');

    // Section title
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(13, 36, 56);
    pdf.text('Financial Overview', x + 10, y + 8);

    // Divider
    pdf.setDrawColor(200, 205, 210);
    pdf.line(x + 10, y + 10, x + width - 10, y + 10);

    // Financial metrics
    const metrics = [
      { label: 'Total Invoiced', value: this.formatCurrency(quotation.price), color: [13, 36, 56] },
      { label: 'Amount Paid', value: this.formatCurrency(quotation.totalPaid), color: [0, 128, 0] },
      { label: 'Outstanding Balance', value: this.formatCurrency(quotation.outstandingAmount), color: [200, 0, 0] },
      { label: 'Payment Progress', value: `${Math.round((quotation.totalPaid / quotation.price) * 100)}%`, color: [13, 36, 56] }
    ];

    const boxWidth = (width - 30) / 4;
    const boxHeight = 30;

    metrics.forEach((metric, index) => {
      const boxX = x + 10 + (index * (boxWidth + 5));

      // Metric box
      pdf.setFillColor(245, 247, 250);
      pdf.roundedRect(boxX, y + 15, boxWidth, boxHeight, 2, 2, 'F');
      pdf.setDrawColor(200, 205, 210);
      pdf.roundedRect(boxX, y + 15, boxWidth, boxHeight, 2, 2, 'D');

      // Metric label
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 105, 110);
      pdf.text(metric.label, boxX + 5, y + 22);

      // Metric value
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(metric.color[0], metric.color[1], metric.color[2]);
      pdf.text(metric.value, boxX + 5, y + 32);

      // Progress bar for payment progress
      if (index === 3) {
        const progress = quotation.price > 0 ? (quotation.totalPaid / quotation.price) : 0;
        const barWidth = boxWidth - 10;
        const barHeight = 4;

        // Background
        pdf.setFillColor(230, 230, 230);
        pdf.rect(boxX + 5, y + 37, barWidth, barHeight, 'F');

        // Progress
        pdf.setFillColor(0, 128, 0);
        pdf.rect(boxX + 5, y + 37, barWidth * progress, barHeight, 'F');
      }
    });

    return y + sectionHeight;
  }
  private addPaymentStatusSection(pdf: jsPDF, x: number, y: number, width: number, quotation: DueQuotation): number {
    const sectionHeight = 25;

    // Determine status
    let statusText: string;
    let statusColor: [number, number, number]; // Tuple type for RGB values
    let statusBg: [number, number, number];   // Tuple type for RGB values

    if (quotation.outstandingAmount <= 0) {
      statusText = 'PAID IN FULL';
      statusColor = [0, 128, 0];
      statusBg = [232, 255, 232];
    } else if (quotation.delayedDays > 0) {
      statusText = `OVERDUE BY ${quotation.delayedDays} DAYS`;
      statusColor = [200, 0, 0];
      statusBg = [255, 232, 232];
    } else {
      statusText = 'PENDING PAYMENT';
      statusColor = [255, 165, 0];
      statusBg = [255, 245, 232];
    }

    // Status container
    pdf.setFillColor(statusBg[0], statusBg[1], statusBg[2]);
    pdf.setDrawColor(statusColor[0], statusColor[1], statusColor[2]);
    pdf.roundedRect(x, y, width, sectionHeight, 3, 3, 'FD');

    // Status text
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(statusColor[0], statusColor[1], statusColor[2]);
    pdf.text(`Payment Status: ${statusText}`, x + 10, y + 12);

    // Due date
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 105, 110);
    pdf.text(`Due Date: ${quotation.dueDate ? quotation.dueDate.toLocaleDateString() : 'N/A'}`, x + width - 10, y + 12, { align: 'right' });

    return y + sectionHeight;
  }

  // Add watermark for professional look
private addProfessionalWatermark(pdf: jsPDF, pageWidth: number, pageHeight: number): void {
  // Subtle watermark
  pdf.setTextColor(250, 250, 250);
  pdf.setFontSize(60);
  pdf.setFont('helvetica', 'bold');

  const centerX = pageWidth / 2;
  const centerY = pageHeight / 2;

  pdf.text('KIKBLOX', centerX, centerY - 10, {
    align: 'center',
    angle: 45
  });

  pdf.setFontSize(20);
  pdf.setTextColor(248, 248, 248);
  pdf.text('CONFIDENTIAL FINANCIAL REPORT', centerX, centerY + 10, {
    align: 'center',
    angle: 45
  });
}


  // Modified footer to handle multiple pages
  private addCorporateFooter(pdf: jsPDF, quotation: DueQuotation, pageWidth: number, pageHeight: number, currentPage: number): void {
    const footerY = pageHeight - 15;

    // Footer background
    pdf.setFillColor(13, 36, 56);
    pdf.rect(0, footerY, pageWidth, 15, 'F');

    // Footer content
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(200, 205, 210);

    // Left footer
    pdf.text('Kikblox Financial Services', 15, footerY + 5);
    pdf.text('confidential', 15, footerY + 10);

    // Center footer
    pdf.text('For inquiries: info@kikblox.com | +94 112 251 111', pageWidth / 2, footerY + 10, { align: 'center' });

    // Right footer
    pdf.text(`Page ${currentPage}`, pageWidth - 15, footerY + 5, { align: 'right' });
    pdf.text(`Generated: ${new Date().toLocaleString()}`, pageWidth - 15, footerY + 10, { align: 'right' });
  }


  // Method to close email dialog
  closeEmailDialog(): void {
    this.showEmailDialog = false;
    this.emailContent = {};
  }

  // Method to copy content to clipboard
  copyToClipboard(content: string): void {
    navigator.clipboard.writeText(content).then(() => {
      this.messageService.add({
        severity: 'success',
        summary: 'Copied',
        detail: 'Content copied to clipboard'
      });
    }).catch(err => {
      console.error('Could not copy text: ', err);
      this.messageService.add({
        severity: 'warn',
        summary: 'Copy Failed',
        detail: 'Please copy manually'
      });
    });
  }



  openInOutlook(): void {
    // First try direct mailto
    try {
      window.location.href = this.mailtoUrl;

      // Fallback to web after delay
      setTimeout(() => {
        this.openInOutlookWeb();
      }, 500);
    } catch (error) {
      this.openInOutlookWeb();
    }
  }

  openInOutlookWeb(): void {
    // First decode the encoded line breaks, then re-encode for URL
    const decodedBody = this.emailContent.body.replace(/%0D%0A/g, '\n');
    const outlookWebUrl = `https://outlook.office.com/mail/deeplink/compose?to=${encodeURIComponent(this.emailContent.to)
      }&subject=${encodeURIComponent(this.emailContent.subject)
      }&body=${encodeURIComponent(decodedBody)
      }`;

    window.open(outlookWebUrl, '_blank', 'noopener,noreferrer');
  }

  private generateReminderSubject(quotation: DueQuotation): string {
    const daysText = quotation.delayedDays > 0
      ? `${quotation.delayedDays} days overdue`
      : quotation.delayedDays === 0
        ? 'due today'
        : `${Math.abs(quotation.delayedDays)} days remaining`;

    return `Payment Reminder - Quotation ${quotation.quotationNo} (${daysText})`;
  }

  private generateReminderBody(quotation: DueQuotation): string {
    const currentDate = new Date().toLocaleDateString();
    const dueDate = quotation.dueDate ? quotation.dueDate.toLocaleDateString() : 'N/A';

    let statusMessage = '';
    if (quotation.delayedDays > 0) {
      statusMessage = `This payment is currently ${quotation.delayedDays} days overdue.`;
    } else if (quotation.delayedDays === 0) {
      statusMessage = 'This payment is due today.';
    } else {
      statusMessage = `This payment is due in ${Math.abs(quotation.delayedDays)} days.`;
    }

    return [
      `Dear ${quotation.customerName},`,
      '',
      'I hope this email finds you well.',
      '',
      'This is a friendly reminder regarding the outstanding payment for:',
      '',
      'Quotation Details:',
      `• Quotation Number: ${quotation.quotationNo}`,
      `• Customer: ${quotation.customerName}`,
      `• Invoice Number: ${quotation.invoiceNo || 'N/A'}`,
      `• Total Amount: ${this.formatCurrency(quotation.price)}`,
      `• Amount Paid: ${this.formatCurrency(quotation.totalPaid)}`,
      `• Outstanding Amount: ${this.formatCurrency(quotation.outstandingAmount)}`,
      `• Due Date: ${dueDate}`,
      '',
      'Payment Status:',
      statusMessage,
      '',
      quotation.delayedDays > 0 ?
        'We kindly request your immediate attention to settle this overdue amount to avoid any inconvenience.' :
        quotation.delayedDays === 0 ?
          'Please ensure payment is processed today to avoid late fees.' :
          'Please mark your calendar and ensure timely payment.',
      '',
      'If you have already processed this payment, please disregard this reminder and accept our apologies for any inconvenience.',
      '',
      'If you have any questions or need to discuss payment arrangements, please don\'t hesitate to contact us.',
      '',
      'Thank you for your prompt attention to this matter.',
      '',
      `This reminder was generated on: ${currentDate}`
    ].join('\n');
  }

  private createMailtoUrl(recipient: string, subject: string, body: string): string {
    const encodedRecipient = encodeURIComponent(recipient);
    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(body.replace(/\n/g, '%0D%0A'));

    return `mailto:${encodedRecipient}?subject=${encodedSubject}&body=${encodedBody}`;
  }
  // Add this at the top of your component class, with other properties
private formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}



  exportToExcel(includeFilters: boolean = true): void {
    try {
      const wb: XLSX.WorkBook = XLSX.utils.book_new();

      // Summary Sheet
      this.createSummarySheet(wb);

      // Main Data Sheet
      this.createDataSheet(wb);

      // Filters Info Sheet (if requested)
      if (includeFilters) {
        this.createFiltersSheet(wb);
      }

      // Generate filename
      const fileName = `due-quotations-report-${this.formatDateForFilename(new Date())}.xlsx`;

      // Save file
      XLSX.writeFile(wb, fileName);

      this.messageService.add({
        severity: 'success',
        summary: 'Export Successful',
        detail: `Complete report exported as "${fileName}"`
      });

    } catch (error) {
      console.error('Error exporting to Excel:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Export Failed',
        detail: 'Failed to export data to Excel. Please try again.'
      });
    }
  }

  private createSummarySheet(wb: XLSX.WorkBook): void {
    const summaryData = [
      ['Due Quotations Summary Report'],
      ['Generated On', new Date().toLocaleString()],
      [''],
      ['Statistics'],
      ['Total Records', this.filteredDueQuotations.length],
      ['Total Due Amount', this.totalDue],
      ['Overdue Amount', this.overdueTotal],
      ['Pending Amount', this.pendingTotal],
      [''],
      ['Status Breakdown'],
      ['Overdue Count', this.filteredDueQuotations.filter(q => q.status === 'Overdue').length],
      ['Pending Count', this.filteredDueQuotations.filter(q => q.status === 'Pending').length],
    ];

    const ws = XLSX.utils.aoa_to_sheet(summaryData);
    ws['!cols'] = [{ wch: 25 }, { wch: 20 }];

    // Apply styles to summary sheet
    this.applySummaryStyles(ws, summaryData);

    XLSX.utils.book_append_sheet(wb, ws, 'Summary');
  }

  private createDataSheet(wb: XLSX.WorkBook): void {
    const exportData = this.filteredDueQuotations.map(quotation => ({
      'Quotation No': quotation.quotationNo,
      'Customer Name': quotation.customerName,
      'Customer Email': quotation.customerEmail,
      'Last Invoice No': quotation.invoiceNo,
      'Last Invoice Date': quotation.lastInvoiceDate ? this.formatDateForExport(quotation.lastInvoiceDate) : '',
      'Due Date': quotation.dueDate ? this.formatDateForExport(quotation.dueDate) : '',
      'Total Invoice Amount': quotation.price,
      'Total Paid': quotation.totalPaid,
      'Outstanding Amount': quotation.outstandingAmount,
      'Status': quotation.status,
      'Delayed Days': quotation.delayedDays,
      'Delay Status': this.getDelayedDaysLabel(quotation.delayedDays)
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);

    // Set column widths
    ws['!cols'] = [
      { wch: 15 }, { wch: 25 }, { wch: 30 }, { wch: 15 },
      { wch: 18 }, { wch: 15 }, { wch: 12 }, { wch: 18 },
      { wch: 15 }, { wch: 18 }, { wch: 12 }, { wch: 12 }, { wch: 20 }
    ];

    // Apply styles to data sheet
    this.applyDataSheetStyles(ws, exportData);

    XLSX.utils.book_append_sheet(wb, ws, 'Due Quotations');
  }

  private createFiltersSheet(wb: XLSX.WorkBook): void {
    const filtersData = [
      ['Applied Filters'],
      [''],
      ['Global Filter', this.globalFilter || 'None'],
      ['Selected Status', this.selectedStatus?.label || 'All'],
      ['Date Range', this.dateRange.length === 2 ?
        `${this.formatDateForExport(this.dateRange[0])} to ${this.formatDateForExport(this.dateRange[1])}` : 'None'],
      ['Delayed Days Range', `${this.delayedDaysRange[0]} to ${this.delayedDaysRange[1]} days`],
      ['Price Range',
        `${this.minPrice || 'No min'} to ${this.maxPrice || 'No max'}`],
      ['Customer Types', this.selectedCustomerTypes.length > 0 ?
        this.selectedCustomerTypes.join(', ') : 'All'],
    ];

    const ws = XLSX.utils.aoa_to_sheet(filtersData);
    ws['!cols'] = [{ wch: 25 }, { wch: 35 }];

    // Apply styles to filters sheet
    this.applyFiltersStyles(ws, filtersData);

    XLSX.utils.book_append_sheet(wb, ws, 'Filters Applied');
  }


  private applyDataSheetStyles(ws: XLSX.WorkSheet, exportData: any[]): void {
    const headers = Object.keys(exportData[0] || {});

    // Header row styling
    headers.forEach((header, colIndex) => {
      const cellRef = XLSX.utils.encode_cell({ c: colIndex, r: 0 });
      if (ws[cellRef]) {
        ws[cellRef].s = {
          font: { bold: true, color: { rgb: "FFFFFF" }, size: 11 },
          fill: { fgColor: { rgb: "2f5597" } },
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } }
          }
        };
      }
    });

    // Data rows styling
    exportData.forEach((row, rowIndex) => {
      const actualRowIndex = rowIndex + 1; // +1 because of header row

      headers.forEach((header, colIndex) => {
        const cellRef = XLSX.utils.encode_cell({ c: colIndex, r: actualRowIndex });
        if (ws[cellRef]) {
          let cellStyle: any = {
            alignment: { horizontal: "left", vertical: "center" },
            border: {
              top: { style: "thin", color: { rgb: "cccccc" } },
              bottom: { style: "thin", color: { rgb: "cccccc" } },
              left: { style: "thin", color: { rgb: "cccccc" } },
              right: { style: "thin", color: { rgb: "cccccc" } }
            }
          };

          // Alternate row colors
          if (actualRowIndex % 2 === 0) {
            cellStyle.fill = { fgColor: { rgb: "f8f9fa" } };
          }

          // Special styling for specific columns
          if (header === 'Status') {
            const status = row[header];
            if (status === 'Overdue') {
              cellStyle.font = { bold: true, color: { rgb: "d63384" } };
              cellStyle.fill = { fgColor: { rgb: "f8d7da" } };
            } else if (status === 'Pending') {
              cellStyle.font = { bold: true, color: { rgb: "198754" } };
              cellStyle.fill = { fgColor: { rgb: "d1e7dd" } };
            }
          }

          // Number formatting for amount columns
          if (header.includes('Amount') || header === 'Total Paid') {
            cellStyle.numFmt = '"$"#,##0.00';
            cellStyle.alignment = { horizontal: "right", vertical: "center" };
          }

          // Number formatting for days
          if (header === 'Delayed Days') {
            cellStyle.alignment = { horizontal: "center", vertical: "center" };
            const days = row[header];
            if (days > 0) {
              cellStyle.font = { color: { rgb: "d63384" } };
            } else if (days < 0) {
              cellStyle.font = { color: { rgb: "198754" } };
            }
          }

          ws[cellRef].s = cellStyle;
        }
      });
    });
  }


  private applyFiltersStyles(ws: XLSX.WorkSheet, filtersData: any[]): void {
    // Title styling
    if (ws['A1']) {
      ws['A1'].s = {
        font: { bold: true, size: 14, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "6c757d" } },
        alignment: { horizontal: "center", vertical: "center" }
      };
    }

    // Filter labels and values styling
    for (let i = 2; i < filtersData.length; i++) {
      const labelCell = XLSX.utils.encode_cell({ c: 0, r: i });
      const valueCell = XLSX.utils.encode_cell({ c: 1, r: i });

      if (ws[labelCell]) {
        ws[labelCell].s = {
          font: { bold: true },
          fill: { fgColor: { rgb: "e9ecef" } },
          alignment: { horizontal: "left", vertical: "center" }
        };
      }

      if (ws[valueCell]) {
        ws[valueCell].s = {
          alignment: { horizontal: "left", vertical: "center" },
          fill: { fgColor: { rgb: "f8f9fa" } }
        };
      }
    }

    // Merge title cell
    ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }];
  }

  private formatDateForExport(date: Date): string {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }

  private formatDateForFilename(date: Date): string {
    return date.toISOString().split('T')[0]; // YYYY-MM-DD format
  }

  private applySummaryStyles(ws: XLSX.WorkSheet, summaryData: any[]): void {
    // Title styling (A1)
    if (ws['A1']) {
      ws['A1'].s = {
        font: { bold: true, size: 16, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "1f4e79" } },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } }
        }
      };
    }

    // Generated On styling (A2)
    if (ws['A2']) {
      ws['A2'].s = {
        font: { italic: true, size: 10 },
        fill: { fgColor: { rgb: "f2f2f2" } }
      };
    }

    // Section headers styling (Statistics, Status Breakdown)
    const sectionHeaders = [3, 9]; // 0-based indices for "Statistics" and "Status Breakdown"
    sectionHeaders.forEach(rowIndex => {
      const cellRef = XLSX.utils.encode_cell({ c: 0, r: rowIndex });
      if (ws[cellRef]) {
        ws[cellRef].s = {
          font: { bold: true, size: 12, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "4472c4" } },
          alignment: { horizontal: "left", vertical: "center" }
        };
      }
    });

    // Data rows styling
    for (let i = 4; i < 8; i++) { // Statistics data rows
      const labelCell = XLSX.utils.encode_cell({ c: 0, r: i });
      const valueCell = XLSX.utils.encode_cell({ c: 1, r: i });

      if (ws[labelCell]) {
        ws[labelCell].s = {
          font: { bold: true },
          fill: { fgColor: { rgb: "e1ecf4" } },
          alignment: { horizontal: "left" }
        };
      }

      if (ws[valueCell]) {
        ws[valueCell].s = {
          alignment: { horizontal: "right" },
          numFmt: i === 4 ? '0' : '"$"#,##0.00' // Number format for counts vs amounts
        };
      }
    }

    // Status breakdown styling
    for (let i = 10; i < summaryData.length; i++) {
      const labelCell = XLSX.utils.encode_cell({ c: 0, r: i });
      const valueCell = XLSX.utils.encode_cell({ c: 1, r: i });

      if (ws[labelCell]) {
        ws[labelCell].s = {
          font: { bold: true },
          fill: { fgColor: { rgb: "fff2cc" } },
          alignment: { horizontal: "left" }
        };
      }

      if (ws[valueCell]) {
        ws[valueCell].s = {
          alignment: { horizontal: "right" },
          numFmt: '0' // Number format for counts
        };
      }
    }

    // Merge title cell across columns
    ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }];
  }


  onGlobalFilter(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.globalFilter = target.value;
    this.table.filterGlobal(target.value, 'contains');
  }

  closeDialog(): void {
    this.ref.close();
  }
  maximizeDialog(): void {
    this.isMaximized = !this.isMaximized;
    this.ref.maximize(this.isMaximized);
  }

}
